import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { getUsuarios, createUsuario, updateUsuario, deleteUsuario, getUsuarioPermisos, updateUsuarioPermisos } from '../../services/muestrasApi';
import ExcelGrid from '../../components/muestras/ExcelGrid';
import { toast } from 'sonner';
import { Users, Plus, Edit2, Trash2, X, Shield, CheckCircle2, XCircle, Key, Download, AlertTriangle } from 'lucide-react';

// Toggle Switch Component
const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-gray-200'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
  </button>
);

// Permission Row Component
const PermissionRow = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm text-gray-700">{label}</span>
    <Toggle checked={checked} onChange={onChange} />
  </div>
);

// Permission Section Component
const PermissionSection = ({ title, prefix, permisos, onToggle, onSetAll }) => {
  const actions = ['ver', 'crear', 'editar', 'eliminar'];
  
  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-800">{title}</h4>
        <div className="flex gap-1">
          <button 
            onClick={() => onSetAll(prefix, true)} 
            className="px-2 py-1 text-xs text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
          >
            Todo
          </button>
          <button 
            onClick={() => onSetAll(prefix, false)} 
            className="px-2 py-1 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Nada
          </button>
        </div>
      </div>
      <div className="space-y-1">
        {actions.map(action => (
          <PermissionRow
            key={action}
            label={action.charAt(0).toUpperCase() + action.slice(1)}
            checked={permisos[`${prefix}_${action}`] || false}
            onChange={() => onToggle(`${prefix}_${action}`)}
          />
        ))}
      </div>
    </div>
  );
};

// Download Permission Section
const DownloadPermissionSection = ({ permisos, onToggle, onSetAll }) => {
  const downloadPermisos = [
    { key: 'descargar_patrones', label: 'Patrones' },
    { key: 'descargar_tizados', label: 'Tizados' },
    { key: 'descargar_fichas', label: 'Fichas' },
    { key: 'descargar_imagenes', label: 'Imágenes' },
    { key: 'descargar_costos', label: 'Archivos de Costos' },
  ];

  return (
    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-100">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Download className="w-4 h-4 text-violet-600" />
          <h4 className="font-semibold text-gray-800">Descargas de Archivos</h4>
        </div>
        <div className="flex gap-1">
          <button 
            onClick={() => onSetAll('descargar', true)} 
            className="px-2 py-1 text-xs text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
          >
            Todo
          </button>
          <button 
            onClick={() => onSetAll('descargar', false)} 
            className="px-2 py-1 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Nada
          </button>
        </div>
      </div>
      <div className="space-y-1">
        {downloadPermisos.map(perm => (
          <PermissionRow
            key={perm.key}
            label={perm.label}
            checked={permisos[perm.key] || false}
            onChange={() => onToggle(perm.key)}
          />
        ))}
      </div>
    </div>
  );
};

const MuestrasUsuarios = () => {
  const { user } = useApp();
  const isAdmin = user?.rol === 'ADMIN' || user?.rol === 'admin' || user?.rol === 'super_admin' || user?.rol === 'SUPER_ADMIN';
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ username: '', email: '', nombre: '', password: '', rol: 'viewer' });
  const [showPermisosModal, setShowPermisosModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermisos, setUserPermisos] = useState({});
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try { setData(await getUsuarios()); } 
    catch (error) { toast.error('Error cargando usuarios'); } 
    finally { setLoading(false); }
  };

  const openModal = (item = null) => {
    setEditingItem(item);
    setFormData({
      username: item?.username || '',
      email: item?.email || '',
      nombre: item?.nombre || '',
      password: '',
      rol: item?.rol || 'viewer'
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.username || (!editingItem && !formData.password)) { 
      toast.error('Usuario y contraseña son requeridos'); return; 
    }
    try {
      if (editingItem) {
        await updateUsuario(editingItem.id, formData);
        toast.success('Usuario actualizado');
      } else {
        await createUsuario(formData);
        toast.success('Usuario creado');
      }
      setShowModal(false);
      loadData();
    } catch (error) { toast.error(error.message); }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try { 
      await deleteUsuario(deleteItem.id); 
      toast.success('Usuario eliminado'); 
      setDeleteItem(null);
      loadData(); 
    } catch (error) { 
      toast.error(error.message); 
    } finally {
      setDeleting(false);
    }
  };

  const openPermisosModal = async (user) => {
    setSelectedUser(user);
    try {
      const permisos = await getUsuarioPermisos(user.id);
      setUserPermisos(permisos);
      setShowPermisosModal(true);
    } catch (error) { toast.error('Error cargando permisos'); }
  };

  const handleSavePermisos = async () => {
    try {
      await updateUsuarioPermisos(selectedUser.id, userPermisos);
      toast.success('Permisos actualizados');
      setShowPermisosModal(false);
    } catch (error) { toast.error(error.message); }
  };

  const togglePermiso = (permiso) => setUserPermisos(prev => ({ ...prev, [permiso]: !prev[permiso] }));

  const setAllPermisos = (prefix, value) => {
    if (prefix === 'descargar') {
      setUserPermisos(prev => ({
        ...prev,
        descargar_patrones: value,
        descargar_tizados: value,
        descargar_fichas: value,
        descargar_imagenes: value,
        descargar_costos: value,
      }));
    } else {
      setUserPermisos(prev => ({
        ...prev,
        [`${prefix}_ver`]: value,
        [`${prefix}_crear`]: value,
        [`${prefix}_editar`]: value,
        [`${prefix}_eliminar`]: value,
      }));
    }
  };

  const columns = useMemo(() => [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { accessorKey: 'username', header: 'USUARIO', size: 120, cell: ({ row }) => <span className="font-medium text-gray-800">{row.original.username}</span> },
    { accessorKey: 'nombre', header: 'NOMBRE', size: 150 },
    { accessorKey: 'email', header: 'EMAIL', size: 180 },
    {
      accessorKey: 'rol', header: 'ROL', size: 120,
      cell: ({ row }) => {
        const colors = { super_admin: 'bg-purple-100 text-purple-700', admin: 'bg-blue-100 text-blue-700', editor: 'bg-green-100 text-green-700', viewer: 'bg-gray-100 text-gray-700' };
        return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${colors[row.original.rol] || colors.viewer}`}><Shield className="w-3 h-3" />{row.original.rol}</span>;
      }
    },
    {
      accessorKey: 'activo', header: 'ESTADO', size: 90,
      cell: ({ row }) => row.original.activo ? (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-medium"><CheckCircle2 className="w-3 h-3" />Activo</span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 rounded-lg text-xs font-medium"><XCircle className="w-3 h-3" />Inactivo</span>
      )
    },
    {
      id: 'acciones', header: 'ACCIONES', size: 120,
      cell: ({ row }) => {
        const isAdmin = ['super_admin', 'admin'].includes(row.original.rol);
        return (
          <div className="flex items-center gap-1">
            <button onClick={() => openModal(row.original)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"><Edit2 className="w-4 h-4" /></button>
            {!isAdmin && <button onClick={() => openPermisosModal(row.original)} className="p-2 text-violet-500 hover:bg-violet-50 rounded-lg transition-colors" title="Permisos"><Key className="w-4 h-4" /></button>}
            <button onClick={() => setDeleteItem(row.original)} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        );
      }
    }
  ], []);

  const permisosGroups = [
    { title: 'Marcas', prefix: 'marcas' },
    { title: 'Tipos', prefix: 'tipos' },
    { title: 'Entalles', prefix: 'entalles' },
    { title: 'Telas', prefix: 'telas' },
    { title: 'Hilos', prefix: 'hilos' },
    { title: 'Muestras', prefix: 'muestras' },
    { title: 'Bases', prefix: 'bases' },
    { title: 'Tizados', prefix: 'tizados' },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-full" data-testid="muestras-usuarios-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-100 rounded-xl">
            <Users className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Usuarios</h1>
            <p className="text-gray-400 text-sm">Gestión de usuarios y permisos</p>
          </div>
        </div>
        <button onClick={() => openModal()} className="px-5 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 flex items-center gap-2 font-medium transition-colors">
          <Plus className="w-4 h-4" />Agregar
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100">
        <ExcelGrid data={data} columns={columns} loading={loading} pageSize={20} />
      </div>

      {/* Modal Crear/Editar Usuario */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{editingItem ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Usuario *</label>
                <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" disabled={!!editingItem} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Contraseña {editingItem ? '' : '*'}</label>
                <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" placeholder={editingItem ? 'Dejar vacío para mantener' : ''} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre</label>
                <input type="text" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Rol</label>
                <select value={formData.rol} onChange={(e) => setFormData({ ...formData, rol: e.target.value })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent">
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                  {isAdmin && <option value="super_admin">Super Admin</option>}
                </select>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancelar</button>
              <button onClick={handleSubmit} className="px-6 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 font-medium transition-colors">{editingItem ? 'Guardar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Permisos - Minimalista como la imagen */}
      {showPermisosModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl max-h-[90vh] overflow-hidden">
            <div className="p-5 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 rounded-lg">
                  <Key className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Permisos de Usuario</h3>
                  <p className="text-sm text-gray-400">{selectedUser?.nombre || selectedUser?.username}</p>
                </div>
              </div>
              <button onClick={() => setShowPermisosModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Grid de secciones de permisos */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {permisosGroups.map(group => (
                  <PermissionSection
                    key={group.prefix}
                    title={group.title}
                    prefix={group.prefix}
                    permisos={userPermisos}
                    onToggle={togglePermiso}
                    onSetAll={setAllPermisos}
                  />
                ))}
              </div>

              {/* Sección de Descargas de Archivos */}
              <DownloadPermissionSection
                permisos={userPermisos}
                onToggle={togglePermiso}
                onSetAll={setAllPermisos}
              />
            </div>

            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setShowPermisosModal(false)} className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancelar</button>
              <button onClick={handleSavePermisos} className="px-6 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 font-medium transition-colors">Guardar Permisos</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">¿Eliminar Usuario?</h3>
              <p className="text-gray-500 mb-6">
                ¿Estás seguro de eliminar al usuario <strong>"{deleteItem.nombre || deleteItem.username}"</strong>? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-center gap-3">
                <button 
                  onClick={() => setDeleteItem(null)} 
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button 
                  onClick={confirmDelete} 
                  className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium disabled:opacity-50"
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MuestrasUsuarios;
